// ============================================================================
// Markdown renderer — pure functions for rendering markdown to HTML
// ============================================================================
// Depends on: hljs (global from CDN), escapeHtml (local)
// wireFileLinks stays in main.js because it references fvOverlay, closeSidebar,
// and loadFvFile which are feature-specific.

export function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Check if a line is a GFM table separator (e.g. |---|:--|---:|)
export function isTableSep(line) {
  const s = line.trim();
  if (!s.includes("-") || !s.includes("|")) return false;
  const cells = s.split("|").map(c => c.trim());
  return cells.every(c => c === "" || /^:?-+:?$/.test(c));
}

// GFM table: header row | separator row | body rows. Returns HTML or null.
// Handles blank lines between header, separator, and body rows.
export function renderTable(lines, startIdx) {
  const splitRow = (l) => {
    let s = l.trim();
    if (s.startsWith("|")) s = s.slice(1);
    if (s.endsWith("|")) s = s.slice(0, -1);
    return s.split("|").map(c => c.trim());
  };
  const headerLine = lines[startIdx];
  if (!headerLine) return null;
  // Find separator: skip blank lines after header
  let sepIdx = startIdx + 1;
  while (sepIdx < lines.length && lines[sepIdx].trim() === "") sepIdx++;
  const sepLine = lines[sepIdx];
  if (!sepLine || !isTableSep(sepLine)) return null;
  const headerCells = splitRow(headerLine);
  // Separator cells go through the same pipe-stripping as data rows —
  // otherwise leading/trailing pipes add phantom cells and every column's
  // alignment is shifted one slot right (e.g. `---:` lands on the wrong th).
  const sepCells = splitRow(sepLine);
  if (headerCells.length === 0) return null;
  const aligns = sepCells.map(c => {
    if (c.startsWith(":") && c.endsWith(":")) return "center";
    if (c.endsWith(":")) return "right";
    return "left";
  });
  let html = '<div class="table-scroll"><table><thead><tr>';
  headerCells.forEach((c, i) => {
    const a = aligns[i] || "left";
    html += `<th style="text-align:${a}">${inlineMd(c)}</th>`;
  });
  html += '</tr></thead><tbody>';
  // Find body rows: skip blank lines after separator, then collect pipe rows
  let i = sepIdx + 1;
  while (i < lines.length) {
    if (lines[i].trim() === "") { i++; continue; }
    if (!lines[i].includes("|")) break;
    const cells = splitRow(lines[i]);
    html += "<tr>";
    for (let j = 0; j < headerCells.length; j++) {
      const a = aligns[j] || "left";
      html += `<td style="text-align:${a}">${inlineMd(cells[j] || "")}</td>`;
    }
    html += "</tr>";
    i++;
    // Skip blank lines between body rows
    while (i < lines.length && lines[i].trim() === "") i++;
  }
  html += "</tbody></table></div>";
  return { html, nextIdx: i };
}

export function renderMarkdown(text) {
  const lines = text.split("\n");
  let html = "";
  let inCode = false, codeLang = "", codeBuf = [];
  let inList = false, listType = "ul";
  const flushList = () => { if (inList) { html += `</${listType}>`; inList = false; } };
  for (let idx = 0; idx < lines.length; idx++) {
    // Expand citation tags on prose lines only — inside code fences the
    // tags are literal examples and must stay verbatim.
    const line = inCode ? lines[idx] : expandRefTags(lines[idx]);
    const fence = line.match(/^```(\w*)/);
    if (fence) {
      if (!inCode) { flushList(); inCode = true; codeLang = fence[1] || ""; codeBuf = []; }
      else {
        const code = codeBuf.join("\n");
        let highlighted;
        if (codeLang && typeof hljs !== "undefined" && hljs.getLanguage(codeLang)) {
          highlighted = hljs.highlight(code, { language: codeLang }).value;
        } else if (typeof hljs !== "undefined") {
          highlighted = hljs.highlightAuto(code).value;
        } else {
          highlighted = escapeHtml(code);
        }
        html += `<pre><code class="hljs${codeLang ? ` language-${codeLang}` : ""}">${highlighted}</code></pre>`;
        inCode = false;
      }
      continue;
    }
    if (inCode) { codeBuf.push(line); continue; }
    if (line.trim() === "") { flushList(); continue; }
    // GFM table: current line has |, and a separator line follows (possibly after blank lines).
    if (line.includes("|") && line.trim() !== "") {
      let peek = idx + 1;
      while (peek < lines.length && lines[peek].trim() === "") peek++;
      if (peek < lines.length && isTableSep(lines[peek])) {
        flushList();
        const t = renderTable(lines, idx);
        if (t) { html += t.html; idx = t.nextIdx - 1; continue; }
      }
    }
    const h = line.match(/^(#{1,3})\s+(.*)/);
    if (h) { flushList(); html += `<h${h[1].length}>${inlineMd(h[2])}</h${h[1].length}>`; continue; }
    if (/^(-{3,}|\*{3,})$/.test(line.trim())) { flushList(); html += "<hr/>"; continue; }
    const ul = line.match(/^[-*]\s+(.*)/);
    const ol = line.match(/^\d+\.\s+(.*)/);
    if (ul) { if (!inList || listType !== "ul") { flushList(); html += "<ul>"; inList = true; listType = "ul"; } html += `<li>${inlineMd(ul[1])}</li>`; continue; }
    if (ol) { if (!inList || listType !== "ol") { flushList(); html += "<ol>"; inList = true; listType = "ol"; } html += `<li>${inlineMd(ol[1])}</li>`; continue; }
    flushList();
    html += `<p>${inlineMd(line)}</p>`;
  }
  if (inCode) html += `<pre><code>${escapeHtml(codeBuf.join("\n"))}</code></pre>`;
  flushList();
  return html;
}

/**
 * Convert <ref_file>/<ref_snippet> citation tags into the same
 * [text](file:///path) markdown links that live ACP updates use.
 */
export function expandRefTags(text) {
  if (!text || text.indexOf("<ref_") === -1) return text;
  const encodePath = (p) => p.replace(/[%\s"'()?#]/g, (c) => "%" + c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0"));
  const shortName = (p) => p.split("/").pop() || p;
  const rel = (p) => encodePath(p.replace(/^\//, ""));
  text = text.replace(
    /<ref_snippet\s+file=["']([^"']+)["']\s+lines=["'](\d+)-(\d+)["']\s*\/>/g,
    (_, p, a, b) => `[${shortName(p)}:${a}-${b}](file:///${rel(p)}?lines=${a}-${b})`
  );
  text = text.replace(
    /<ref_file\s+file=["']([^"']+)["']\s*\/>/g,
    (_, p) => `[${shortName(p)}](file:///${rel(p)})`
  );
  return text;
}

export function inlineMd(s) {
  let r = escapeHtml(s);
  r = r.replace(/`([^`]+)`/g, "<code>$1</code>");
  r = r.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  r = r.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");
  r = r.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  // File links: [text](file:///absolute/path) → clickable, opens in file viewer.
  r = r.replace(
    /\[([^\]]+)\]\(file:\/\/\/([^\s)?]+)(?:\?lines=(\d+)-(\d+))?\)/g,
    (_, label, path, a, b) =>
      `<a class="file-link" data-filepath="/${path}"${a ? ` data-lines="${a}-${b}"` : ""}>${label}</a>`
  );
  return r;
}
